/**
 * Centralized Analytics Service for Interview Platform
 *
 * Abstracts Google Analytics away from components.
 * Uses ReactGA (react-ga) which is already installed in the project.
 * Can be swapped for Mixpanel, Amplitude, GA4, etc. later by changing this single file.
 *
 * Event Categories:
 * - LoginFlow: Invitation & login events (Stage 1)
 * - DeviceSetup: Camera, mic, screen share events (Stage 2)
 * - Lobby: Lobby & admission events (Stage 3)
 * - InterviewSession: Session lifecycle events (Stage 4)
 * - Communication: Mic, camera, chat engagement events
 * - CodeEditor: Code editor interaction events
 * - ProblemInteraction: Problem lifecycle events
 * - Stability: Network disconnection & reconnection events
 * - ExitRejoin: Leave, rejoin, abandonment events
 * - API: API failure tracking
 */
import ReactGA from 'react-ga4';

const analytics = {
  /**
   * Reads the current user & interview context from localStorage.
   * Returns an object that can be spread into GA custom dimensions.
   */
  _getUserContext() {
    return {
      interview_id: localStorage.getItem('interview_id') || undefined,
      role: localStorage.getItem('role') || undefined,
      email: localStorage.getItem('username') || undefined,
      company_id: localStorage.getItem('company_id') || undefined,
    };
  },

  /**
   * Call this after login / invite validation so GA associates
   * all subsequent hits with the user. Sets GA userId and
   * custom dimensions that persist for the page session.
   */
  setUserContext() {
    const email = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    const companyId = localStorage.getItem('company_id');
    const interviewId = localStorage.getItem('interview_id');

    const dims = {};
    if (email) dims.user_id = email;
    if (role) dims.role = role;
    if (companyId) dims.company_id = companyId;
    if (interviewId) dims.interview_id = interviewId;

    if (Object.keys(dims).length) {
      ReactGA.set(dims);
    }
  },

  /**
   * Every event is automatically enriched with the current user context
   * (interview_id, role, email, company_id) from localStorage.
   * The context is appended to the label so it's visible in GA reports
   * even without custom dimensions being configured in the GA admin panel.
   */
  trackEvent(category, action, label, value) {
    const ctx = this._getUserContext();
    // Build a context suffix string from available fields
    const ctxParts = [];
    if (ctx.interview_id) ctxParts.push(`iid:${ctx.interview_id}`);
    if (ctx.role) ctxParts.push(`role:${ctx.role}`);
    if (ctx.email) ctxParts.push(`email:${ctx.email}`);
    if (ctx.company_id) ctxParts.push(`cid:${ctx.company_id}`);
    const ctxSuffix = ctxParts.length ? ctxParts.join('|') : '';

    // Append context to label (if label exists, separate with " :: ")
    const enrichedLabel = label
      ? `${label}${ctxSuffix ? ' :: ' + ctxSuffix : ''}`
      : ctxSuffix || undefined;

    const eventPayload = {
      category,
      action,
      ...(enrichedLabel && { label: enrichedLabel }),
      ...(value !== undefined && { value }),
    };

    ReactGA.event(action, {
      event_category: category,
      ...(enrichedLabel && { event_label: enrichedLabel }),
      ...(value !== undefined && { value }),
    });
  },

  trackInterviewLinkClicked(interviewId) {
    this.trackEvent('LoginFlow', 'interview_link_clicked', interviewId);
  },

  trackLoginMethodSelected(method) {
    // method: 'otp' | 'google'
    this.trackEvent('LoginFlow', 'login_method_selected', method);
  },

  trackOtpRequested() {
    this.trackEvent('LoginFlow', 'otp_requested');
  },

  trackOtpVerifiedSuccess() {
    this.trackEvent('LoginFlow', 'otp_verified_success');
  },

  trackOtpVerifiedFailure(failureReason) {
    // failureReason: 'expired' | 'incorrect_code'
    this.trackEvent('LoginFlow', 'otp_verified_failure', failureReason);
  },

  trackGoogleLoginSuccess() {
    this.trackEvent('LoginFlow', 'google_login_success');
  },

  trackGoogleLoginFailure(errorMessage) {
    this.trackEvent('LoginFlow', 'google_login_failure', errorMessage);
  },

  trackCameraPermissionGranted() {
    this.trackEvent('DeviceSetup', 'camera_permission_granted');
  },

  trackCameraPermissionDenied() {
    this.trackEvent('DeviceSetup', 'camera_permission_denied');
  },

  trackMicrophonePermissionGranted() {
    this.trackEvent('DeviceSetup', 'microphone_permission_granted');
  },

  trackMicrophonePermissionDenied() {
    this.trackEvent('DeviceSetup', 'microphone_permission_denied');
  },

  trackScreenShareStarted() {
    this.trackEvent('DeviceSetup', 'screen_share_started');
  },

  trackScreenShareFailed(errorMessage) {
    this.trackEvent('DeviceSetup', 'screen_share_failed', errorMessage);
  },

  trackDeviceSetupCompleted() {
    this.trackEvent('DeviceSetup', 'device_setup_completed');
  },

  trackEnteredLobby(interviewId) {
    this.trackEvent('Lobby', 'entered_lobby', interviewId);
  },

  trackAdmittedToInterview(interviewId) {
    this.trackEvent('Lobby', 'admitted_to_interview', interviewId);
  },

  trackInterviewSessionStarted(interviewId) {
    this.trackEvent('InterviewSession', 'interview_session_started', interviewId);
  },

  trackInterviewSessionEnded(interviewId, duration, exitType) {
    // exitType: 'completed' | 'manual_leave' | 'disconnected'
    this.trackEvent(
      'InterviewSession',
      'interview_session_ended',
      `${interviewId}|${exitType}`,
      duration,
    );
  },

  trackMicToggled(state) {
    // state: 'on' | 'off'
    this.trackEvent('Communication', 'mic_toggled', state);
  },

  trackCameraToggled(state) {
    // state: 'on' | 'off'
    this.trackEvent('Communication', 'camera_toggled', state);
  },

  trackChatMessageSent(messageLength) {
    this.trackEvent('Communication', 'chat_message_sent', undefined, messageLength);
  },

  trackScreenShareStopped() {
    this.trackEvent('Communication', 'screen_share_stopped');
  },

  trackCodeEditorLoaded(interviewId) {
    this.trackEvent('CodeEditor', 'code_editor_loaded', interviewId);
  },

  trackCodeWritten(linesAdded, charactersAdded) {
    this.trackEvent('CodeEditor', 'code_written', `lines:${linesAdded}|chars:${charactersAdded}`);
  },

  trackCodeRunClicked(language, problemId) {
    this.trackEvent('CodeEditor', 'code_run_clicked', `${language}|${problemId}`);
  },

  trackCodeExecutionResult(status, language) {
    // status: 'success' | 'compile_error' | 'runtime_error' | 'failure' | 'time_exceeded'
    this.trackEvent('CodeEditor', 'code_execution_result', `${status}|${language}`);
  },

  trackLanguageChanged(language, problemId) {
    this.trackEvent('CodeEditor', 'language_changed', `${language}|${problemId}`);
  },

  trackCustomInputUsed() {
    this.trackEvent('CodeEditor', 'custom_input_used');
  },

  trackThemeChanged(theme) {
    // theme: 'light' | 'dark'
    this.trackEvent('CodeEditor', 'theme_changed', theme);
  },

  trackFullscreenEnabled() {
    this.trackEvent('CodeEditor', 'fullscreen_enabled');
  },

  trackFullscreenDisabled() {
    this.trackEvent('CodeEditor', 'fullscreen_disabled');
  },

  trackProblemAssigned(problemId, interviewId) {
    this.trackEvent('ProblemInteraction', 'problem_assigned', `${problemId}|${interviewId}`);
  },

  trackProblemOpened(problemId) {
    this.trackEvent('ProblemInteraction', 'problem_opened', problemId);
  },

  trackProblemSubmitted(problemId, finalStatus, totalRunAttempts) {
    this.trackEvent(
      'ProblemInteraction',
      'problem_submitted',
      `${problemId}|${finalStatus}`,
      totalRunAttempts,
    );
  },

  trackResultsViewed(problemId) {
    this.trackEvent('ProblemInteraction', 'results_viewed', problemId);
  },

  trackNetworkDisconnected() {
    this.trackEvent('Stability', 'network_disconnected');
  },

  // WebSocket connection tracking
  trackWsConnected(wsType) {
    // wsType: 'collab_editor' | 'code_execution' | 'chime_events'
    this.trackEvent('Stability', 'ws_connected', wsType);
  },

  trackWsDisconnected(wsType, code, reason) {
    // code: WebSocket close code (1000 = normal, 1006 = abnormal)
    this.trackEvent(
      'Stability',
      'ws_disconnected',
      `${wsType}|code:${code}|${reason || 'no_reason'}`,
    );
  },

  trackWsError(wsType) {
    this.trackEvent('Stability', 'ws_error', wsType);
  },

  trackWsReconnectAttempted(wsType, attemptNumber) {
    this.trackEvent('Stability', 'ws_reconnect_attempted', wsType, attemptNumber);
  },

  trackAutoReconnectAttempted() {
    this.trackEvent('Stability', 'auto_reconnect_attempted');
  },

  trackReconnectSuccess() {
    this.trackEvent('Stability', 'reconnect_success');
  },

  trackReconnectFailed() {
    this.trackEvent('Stability', 'reconnect_failed');
  },

  trackManualReconnectClicked() {
    this.trackEvent('Stability', 'manual_reconnect_clicked');
  },

  trackLeaveMeetingClicked(interviewId) {
    this.trackEvent('ExitRejoin', 'leave_meeting_clicked', interviewId);
  },

  trackInterviewRejoined(interviewId) {
    this.trackEvent('ExitRejoin', 'interview_rejoined', interviewId);
  },

  trackInterviewAbandoned(interviewId) {
    this.trackEvent('ExitRejoin', 'interview_abandoned', interviewId);
  },

  trackApiFailure(endpoint, status, method) {
    this.trackEvent('API', 'api_failure', `${method} ${endpoint}`, status);
  },
};

export default analytics;
